import React, { createContext, useContext, useMemo, useState } from 'react';
import { apiClient } from '../lib/api';
import { useAuth } from './AuthContext';
import {
  type ImpersonationState,
  type ImpersonationTarget,
  getImpersonationState,
  setImpersonation,
  clearImpersonation,
} from '../lib/impersonation';

interface ImpersonationContextType {
  state: ImpersonationState | null;
  isImpersonating: boolean;
  /** Open/close state for the user-picker modal. */
  pickerOpen: boolean;
  openPicker: () => void;
  closePicker: () => void;
  /** Start (or switch) impersonating a target user. Triggers a full reload. */
  startImpersonation: (target: ImpersonationTarget) => Promise<void>;
  /** Stop impersonating and return to the real Super Admin. Triggers a reload. */
  stopImpersonation: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const ctx = useContext(ImpersonationContext);
  if (ctx === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return ctx;
};

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [state] = useState<ImpersonationState | null>(() => getImpersonationState());
  const [pickerOpen, setPickerOpen] = useState(false);

  const startImpersonation = async (target: ImpersonationTarget) => {
    await apiClient.post('/api/impersonation/start/', { user_id: target.id });

    // The real admin is whoever the session already had recorded (when
    // switching mid-impersonation) or the currently authenticated user.
    const existing = getImpersonationState();
    const impersonator =
      existing?.impersonator ??
      (user
        ? {
            id: user.id,
            username: user.username,
            email: user.email,
            name: user.displayName || user.username,
          }
        : null);

    setImpersonation({ target, impersonator });
    // Full reload: org-scoped React Query caches must be discarded so the
    // entire app re-fetches as the target user's organization.
    window.location.assign('/dashboard');
  };

  const stopImpersonation = async () => {
    const current = getImpersonationState();
    // Clear FIRST so the API client stops sending the impersonation header
    // and the stop request authenticates as the real Super Admin.
    clearImpersonation();
    try {
      await apiClient.post('/api/impersonation/stop/', {
        user_id: current?.target?.id,
      });
    } catch {
      // Audit close is best-effort; the session is already cleared locally.
    }
    window.location.assign('/dashboard');
  };

  const value = useMemo<ImpersonationContextType>(
    () => ({
      state,
      isImpersonating: !!state,
      pickerOpen,
      openPicker: () => setPickerOpen(true),
      closePicker: () => setPickerOpen(false),
      startImpersonation,
      stopImpersonation,
    }),
    [state, pickerOpen, user],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};
